<template>
  <div class="checkbox-buttons-group">
    <label v-if="label" :for="name" class="form-label">
      {{ $t(label) }}
      <span v-if="is_required" class="required-asterisk">*</span>
    </label>
    <ValidationProvider
      v-slot="{ errors, failed }"
      :rules="validationRules"
      :name="field"
      :vid="field"
      ref="providerRef"
    >
      <div class="checkbox-buttons-wrapper" :class="{ 'has-error': failed }">
        <div
          v-for="item in items"
          :key="item.value"
          class="checkbox-buttons"
          :class="{
            selected: isSelected(item.value),
            disabled: item.disabled || disabled,
          }"
          @click="toggle(item.value)"
        >
          <input
            type="checkbox"
            :id="item.value"
            :value="item.value"
            :disabled="item.disabled || disabled"
            v-model="localValue"
            class="checkbox"
            :name="name"
          />
          {{ item.text }}
        </div>
      </div>
      <!-- Error handling -->
      <!-- <div v-if="showError" class="error-text">
      {{ errors }}
    </div> -->
      <div v-if="errors.length" class="error-text">
        {{ errors[0] }}
      </div>
    </ValidationProvider>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate'

export default {
  components: {
    ValidationProvider,
  },
  emits: ['change-value'],
  props: {
    items: {
      type: Array,
      required: true,
    },
    value: {
      type: Array,
      default: () => [],
    },
    field: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    is_required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: [String, Object],
      default: '',
    },
  },
  data() {
    return {
      localValue: [...(this.value || [])],
      errorMessage: '',
    }
  },
  watch: {
    value: {
      handler(newVal) {
        // Ensure newVal is an array and only update if different
        const safeNewVal = Array.isArray(newVal) ? newVal : []
        if (JSON.stringify(safeNewVal) !== JSON.stringify(this.localValue)) {
          this.localValue = [...safeNewVal]
        }
      },
      deep: true,
      immediate: true,
    },
    localValue: {
      handler(newVal) {
        this.$emit('change-value', [...newVal], this.field)
      },
      deep: true,
    },
  },
  computed: {
    showError() {
      return this.errorMessage !== ''
    },
    validationRules() {
      let rules = this.rules || ''

      if (this.is_required) {
        rules = rules ? `${rules}` : {}
      }

      return rules
    },
  },
  methods: {
    isSelected(value) {
      return this.localValue.includes(value)
    },
    toggle(value) {
      if (this.disabled) return

      const index = this.localValue.indexOf(value)
      if (index >= 0) {
        this.localValue.splice(index, 1)
      } else {
        this.localValue.push(value)
      }
      // Reset error if at least one item selected
      if (this.localValue.length > 0 && this.$refs.providerRef) {
        this.$refs.providerRef.reset()
      }
    },
  },
  created() {
    // Initialize localValue with a copy of the value prop
    if (this.$validator && !this.$validator.rules.required_array) {
      this.$validator.extend('required_array', {
        validate: (value, args) => {
          return Array.isArray(value) && value.length > 0
        },
        getMessage: (field, params, data) => {
          return params[0] || `The ${field} field is required.`
        },
      })
    }
  },
}
</script>

<style scoped lang="scss">
.checkbox-buttons-group {
  .form-label {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    color: var(--color-115);
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0.25px;
  }

  .required-asterisk {
    color: var(--color-23);
    font-weight: 400;
    margin: 0 4px;
  }

  .checkbox-buttons-wrapper {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    &.has-error {
      .checkbox-buttons {
        border-color: var(--color-47) !important;
      }
    }
  }

  .checkbox-buttons {
    border-radius: 4px;
    border: 1px solid var(--color-120);
    display: flex;
    padding: 10px 12px;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 15px;
    user-select: none;
    transition: all 0.2s ease;
  }

  .checkbox-buttons.selected {
    background-color: var(--color-04);
    border-color: var(--color-23);
    color: var(--color-115);
    opacity: 1 !important;
  }

  .checkbox-buttons.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .checkbox {
    width: 18px;
    height: 18px;

    &::before {
      width: 18px;
      height: 18px;
    }
  }
}
</style>
